import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Privacy Policy</h1>
        <p className="page-subtitle">How we collect, use, and protect your information</p>
        <p className="last-updated">Last updated: January 15, 2025</p>
      </div>

      <div className="page-content legal-content">
        <section className="content-section">
          <h2>1. Introduction</h2>
          <p>
            At Flow ("we," "our," or "us"), we are committed to protecting your privacy and personal information.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
            use our writing platform and services.
          </p>
        </section>

        <section className="content-section">
          <h2>2. Information We Collect</h2>

          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li>Name and email address (when you create an account)</li>
            <li>Profile information and bio (optional)</li>
            <li>Payment information (processed securely through third-party providers)</li>
            <li>Communication preferences and settings</li>
          </ul>

          <h3>2.2 Content Information</h3>
          <ul>
            <li>Articles, drafts, and other content you create on our platform</li>
            <li>Comments and feedback you provide</li>
            <li>Collaboration and sharing activities</li>
          </ul>

          <h3>2.3 Usage Information</h3>
          <ul>
            <li>Device information (browser type, operating system)</li>
            <li>Log data (IP address, access times, pages viewed)</li>
            <li>Usage patterns and feature interactions</li>
            <li>Performance and error data</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>3. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide and maintain our writing platform services</li>
            <li>Create and manage your user account</li>
            <li>Process transactions and billing</li>
            <li>Improve our platform and develop new features</li>
            <li>Provide customer support and respond to inquiries</li>
            <li>Send important updates and security notifications</li>
            <li>Analyze usage patterns to enhance user experience</li>
            <li>Prevent fraud and ensure platform security</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>4. Information Sharing and Disclosure</h2>
          <p>We do not sell, trade, or rent your personal information. We may share your information only in these circumstances:</p>

          <h3>4.1 With Your Consent</h3>
          <p>When you explicitly choose to publish content or share it with other users.</p>

          <h3>4.2 Service Providers</h3>
          <p>With trusted third-party service providers who help us operate our platform (hosting, analytics, payment processing).</p>

          <h3>4.3 Legal Requirements</h3>
          <p>When required by law, court order, or to protect our rights and safety.</p>

          <h3>4.4 Business Transfers</h3>
          <p>In connection with a merger, acquisition, or sale of assets (with notice to users).</p>
        </section>

        <section className="content-section">
          <h2>5. Data Security</h2>
          <p>
            We implement appropriate technical and organizational measures to protect your information:
          </p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication measures</li>
            <li>Secure backup and disaster recovery procedures</li>
            <li>Employee training on data protection best practices</li>
          </ul>
          <p>
            While we strive to protect your information, no method of transmission over the internet
            or electronic storage is 100% secure.
          </p>
        </section>

        <section className="content-section">
          <h2>6. Your Rights and Choices</h2>
          <p>You have the following rights regarding your information:</p>
          <ul>
            <li><strong>Access:</strong> Request a copy of your personal information</li>
            <li><strong>Correction:</strong> Update or correct inaccurate information</li>
            <li><strong>Deletion:</strong> Request deletion of your account and associated data</li>
            <li><strong>Portability:</strong> Export your content and data</li>
            <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            <li><strong>Privacy Settings:</strong> Control who can see your published content</li>
          </ul>
        </section>

        <section className="content-section">
          <h2>7. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies to enhance your experience on our platform.
            These help us remember your preferences, analyze usage patterns, and provide personalized content.
            You can manage cookie preferences through your browser settings.
          </p>
        </section>

        <section className="content-section">
          <h2>8. Third-Party Services</h2>
          <p>
            Our platform may integrate with third-party services (authentication, analytics, payment processing).
            These services have their own privacy policies, and we encourage you to review them.
            We are not responsible for the privacy practices of third-party services.
          </p>
        </section>

        <section className="content-section">
          <h2>9. Children's Privacy</h2>
          <p>
            Our services are not intended for children under 13 years of age. We do not knowingly
            collect personal information from children under 13. If you become aware that a child
            has provided us with personal information, please contact us immediately.
          </p>
        </section>

        <section className="content-section">
          <h2>10. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own.
            We ensure appropriate safeguards are in place to protect your information during such transfers,
            in accordance with applicable data protection laws.
          </p>
        </section>

        <section className="content-section">
          <h2>11. Data Retention</h2>
          <p>
            We retain your information only as long as necessary to provide our services and fulfill
            the purposes outlined in this policy. When you delete your account, we will delete or
            anonymize your personal information, though some information may be retained for legal
            or business purposes.
          </p>
        </section>

        <section className="content-section">
          <h2>12. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any material
            changes by posting the new policy on this page and updating the "last updated" date.
            We encourage you to review this policy periodically.
          </p>
        </section>

        <section className="content-section">
          <h2>13. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our privacy practices, please contact us:
          </p>
          <ul>
            <li>Email: privacy@flow-writing.com</li>
            <li>Through our <a href="/contact">Contact Us</a> page</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;